import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { api } from '../../services/api';

// --- Types ---

interface SiteContentItem {
  [key: string]: unknown;
}

interface SiteContentSection {
  id?: number;
  section: string;
  title: string;
  subtitle: string;
  body: string;
  items: SiteContentItem[];
  image_base64: string | null;
  is_active: boolean;
  sort_order: number;
}

interface SiteContentListResponse {
  data: SiteContentSection[];
}

interface SiteContentResponse {
  data: SiteContentSection;
}

// --- Constants ---

const VALID_SECTIONS = [
  'hero',
  'features',
  'how_it_works',
  'plans',
  'testimonials',
  'stats',
  'contact',
] as const;

const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero Banner',
  features: 'Funcionalidades',
  how_it_works: 'Como funciona',
  plans: 'Planes',
  testimonials: 'Testimonios',
  stats: 'Estadisticas',
  contact: 'Contacto',
};

// --- Component ---

export default function SiteContentPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<SiteContentSection | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSectionType, setNewSectionType] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; section: SiteContentSection | null }>({
    open: false,
    section: null,
  });

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formSubtitle, setFormSubtitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formImage, setFormImage] = useState('');
  const [formItems, setFormItems] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [itemsError, setItemsError] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Modal refs
  const editModalRef = useRef<HTMLDialogElement>(null);
  const createModalRef = useRef<HTMLDialogElement>(null);
  const confirmModalRef = useRef<HTMLDialogElement>(null);

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // --- Sync modals ---
  useEffect(() => {
    if (editOpen) editModalRef.current?.showModal();
    else editModalRef.current?.close();
  }, [editOpen]);

  useEffect(() => {
    if (createOpen) createModalRef.current?.showModal();
    else createModalRef.current?.close();
  }, [createOpen]);

  useEffect(() => {
    if (confirmDelete.open) confirmModalRef.current?.showModal();
    else confirmModalRef.current?.close();
  }, [confirmDelete.open]);

  // --- Query ---

  const { data: contentData, isLoading } = useQuery({
    queryKey: ['admin-site-content'],
    queryFn: () => api.get<SiteContentListResponse>('/v1/site-content/all'),
  });

  const sections = (contentData?.data || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // --- Mutations ---

  const saveMutation = useMutation({
    mutationFn: (data: { section: string; payload: Partial<SiteContentSection> }) =>
      api.put<SiteContentResponse>(`/v1/site-content/${data.section}`, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-content'] });
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
      setEditOpen(false);
      setEditingSection(null);
      setSnackbar({ open: true, message: 'Seccion guardada correctamente', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al guardar la seccion', severity: 'error' });
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<SiteContentSection>) =>
      api.put<SiteContentResponse>(`/v1/site-content/${data.section}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-content'] });
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
      setCreateOpen(false);
      setNewSectionType('');
      setSnackbar({ open: true, message: 'Seccion creada correctamente', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al crear la seccion', severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (section: string) => api.delete(`/v1/site-content/${section}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-content'] });
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
      setConfirmDelete({ open: false, section: null });
      setSnackbar({ open: true, message: 'Seccion eliminada correctamente', severity: 'success' });
    },
    onError: () => {
      setConfirmDelete({ open: false, section: null });
      setSnackbar({ open: true, message: 'Error al eliminar la seccion', severity: 'error' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (data: { section: string; is_active: boolean }) =>
      api.put<SiteContentResponse>(`/v1/site-content/${data.section}`, { is_active: data.is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-content'] });
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al actualizar estado', severity: 'error' });
    },
  });

  // --- Handlers ---

  const handleEdit = (section: SiteContentSection): void => {
    setEditingSection(section);
    setFormTitle(section.title || '');
    setFormSubtitle(section.subtitle || '');
    setFormBody(section.body || '');
    setFormImage(section.image_base64 || '');
    setFormItems(JSON.stringify(section.items || [], null, 2));
    setFormIsActive(section.is_active);
    setFormSortOrder(section.sort_order ?? 0);
    setItemsError('');
    setEditOpen(true);
  };

  const handleSave = (): void => {
    if (!editingSection) return;

    // Validate items JSON
    let parsedItems: SiteContentItem[] = [];
    if (formItems.trim()) {
      try {
        parsedItems = JSON.parse(formItems);
        if (!Array.isArray(parsedItems)) {
          setItemsError('Items debe ser un arreglo JSON valido');
          return;
        }
      } catch {
        setItemsError('JSON invalido. Verifica la sintaxis.');
        return;
      }
    }

    setItemsError('');
    saveMutation.mutate({
      section: editingSection.section,
      payload: {
        title: formTitle,
        subtitle: formSubtitle,
        body: formBody,
        image_base64: formImage || null,
        items: parsedItems,
        is_active: formIsActive,
        sort_order: formSortOrder,
      },
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Max 2MB
    if (file.size > 2 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'La imagen no debe superar 2MB', severity: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (): void => {
      setFormImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleCreate = (): void => {
    if (!newSectionType) return;
    createMutation.mutate({
      section: newSectionType,
      title: SECTION_LABELS[newSectionType] || newSectionType,
      subtitle: '',
      body: '',
      items: [],
      image_base64: null,
      is_active: false,
      sort_order: sections.length * 10 + 10,
    });
  };

  const existingSectionTypes = new Set(sections.map((s) => s.section));
  const availableSectionTypes = VALID_SECTIONS.filter((s) => !existingSectionTypes.has(s));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Contenido del Sitio</h1>
          <p className="text-sm text-base-content/60">
            Administra el contenido de la pagina de inicio corporativa
          </p>
        </div>
        {availableSectionTypes.length > 0 && (
          <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Agregar seccion
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-base-100 rounded-lg">
        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : sections.length === 0 ? (
          <div className="text-center py-12 text-base-content/60">
            <p className="text-lg mb-2">No hay secciones configuradas</p>
            <p className="text-sm">Agrega secciones para personalizar la pagina de inicio</p>
          </div>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Orden</th>
                <th>Seccion</th>
                <th>Titulo</th>
                <th>Estado</th>
                <th>Items</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <tr key={section.section}>
                  <td>
                    <span className="badge badge-ghost badge-sm">{section.sort_order}</span>
                  </td>
                  <td>
                    <span className="font-medium">{SECTION_LABELS[section.section] || section.section}</span>
                    <br />
                    <span className="text-xs text-base-content/50">{section.section}</span>
                  </td>
                  <td className="max-w-xs truncate">{section.title}</td>
                  <td>
                    <input
                      type="checkbox"
                      className="toggle toggle-primary toggle-sm"
                      checked={section.is_active}
                      onChange={() =>
                        toggleActiveMutation.mutate({
                          section: section.section,
                          is_active: !section.is_active,
                        })
                      }
                    />
                  </td>
                  <td>
                    <span className="badge badge-outline badge-sm">
                      {(section.items || []).length} items
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-ghost btn-xs"
                        title="Editar"
                        onClick={() => handleEdit(section)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        className="btn btn-ghost btn-xs text-error"
                        title="Eliminar"
                        onClick={() => setConfirmDelete({ open: true, section })}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Section Modal */}
      <dialog ref={editModalRef} className="modal" onClose={() => setEditOpen(false)}>
        <div className="modal-box max-w-3xl">
          <h3 className="font-bold text-lg mb-4">
            Editar: {SECTION_LABELS[editingSection?.section || ''] || editingSection?.section}
          </h3>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Title */}
            <div className="form-control w-full">
              <label className="label"><span className="label-text">Titulo</span></label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </div>

            {/* Subtitle */}
            <div className="form-control w-full">
              <label className="label"><span className="label-text">Subtitulo</span></label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={formSubtitle}
                onChange={(e) => setFormSubtitle(e.target.value)}
              />
            </div>

            {/* Body (Rich text) */}
            <div className="form-control w-full">
              <label className="label"><span className="label-text">Contenido (HTML)</span></label>
              <div className="bg-base-100 rounded-lg border border-base-300">
                <ReactQuill
                  theme="snow"
                  value={formBody}
                  onChange={setFormBody}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              </div>
            </div>

            {/* Image upload */}
            <div className="form-control w-full">
              <label className="label"><span className="label-text">Imagen (opcional, max 2MB)</span></label>
              <input
                type="file"
                className="file-input file-input-bordered w-full"
                accept="image/*"
                onChange={handleImageUpload}
              />
              {formImage && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={formImage} alt="Preview" className="h-16 rounded border border-base-300" />
                  <button
                    className="btn btn-ghost btn-xs text-error"
                    onClick={() => setFormImage('')}
                  >
                    Quitar imagen
                  </button>
                </div>
              )}
            </div>

            {/* Items (JSON) */}
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Items (JSON array)</span>
                <span className="label-text-alt text-base-content/50">
                  Arreglo de objetos con campos segun el tipo de seccion
                </span>
              </label>
              <textarea
                className={`textarea textarea-bordered w-full h-48 font-mono text-sm ${itemsError ? 'textarea-error' : ''}`}
                value={formItems}
                onChange={(e) => {
                  setFormItems(e.target.value);
                  setItemsError('');
                }}
                placeholder='[{"title": "...", "description": "..."}]'
              />
              {itemsError && (
                <label className="label">
                  <span className="label-text-alt text-error">{itemsError}</span>
                </label>
              )}
            </div>

            {/* Active + Sort Order */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="form-control">
                <label className="label cursor-pointer gap-3">
                  <span className="label-text">Activo</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={formIsActive}
                    onChange={(e) => setFormIsActive(e.target.checked)}
                  />
                </label>
              </div>
              <div className="form-control flex-1">
                <label className="label"><span className="label-text">Orden</span></label>
                <input
                  type="number"
                  className="input input-bordered w-full sm:w-32"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="modal-action">
            <button type="button" className="btn" onClick={() => setEditOpen(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveMutation.isPending}
              onClick={handleSave}
            >
              {saveMutation.isPending ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setEditOpen(false)}>close</button>
        </form>
      </dialog>

      {/* Create Section Modal */}
      <dialog ref={createModalRef} className="modal" onClose={() => setCreateOpen(false)}>
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">Agregar seccion</h3>

          <div className="form-control w-full mb-4">
            <label className="label"><span className="label-text">Tipo de seccion</span></label>
            <select
              className="select select-bordered w-full"
              value={newSectionType}
              onChange={(e) => setNewSectionType(e.target.value)}
            >
              <option value="" disabled>
                Seleccionar tipo...
              </option>
              {availableSectionTypes.map((s) => (
                <option key={s} value={s}>
                  {SECTION_LABELS[s] || s}
                </option>
              ))}
            </select>
          </div>

          <div className="modal-action">
            <button type="button" className="btn" onClick={() => setCreateOpen(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!newSectionType || createMutation.isPending}
              onClick={handleCreate}
            >
              {createMutation.isPending ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Crear'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setCreateOpen(false)}>close</button>
        </form>
      </dialog>

      {/* Confirm Delete Modal */}
      <dialog ref={confirmModalRef} className="modal" onClose={() => setConfirmDelete({ open: false, section: null })}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">Eliminar seccion</h3>
          <p className="py-4 text-base-content/70">
            Estas seguro de eliminar la seccion{' '}
            <strong>{SECTION_LABELS[confirmDelete.section?.section || ''] || confirmDelete.section?.section}</strong>?
            Esta accion no se puede deshacer.
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setConfirmDelete({ open: false, section: null })}>
              Cancelar
            </button>
            <button
              className="btn btn-error"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (confirmDelete.section) {
                  deleteMutation.mutate(confirmDelete.section.section);
                }
              }}
            >
              {deleteMutation.isPending ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Eliminar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setConfirmDelete({ open: false, section: null })}>close</button>
        </form>
      </dialog>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
